import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Brand } from "../../configurations";
import { ROOT } from "../../constants";
import { AppFeatures } from "../../constants/featureFlags";
import { useFeature } from "../../hooks/useFeature";
import { UserContextSelectors } from "../../redux/selectors";
import { PageContextUtils, UrlUtils } from "../../utils";

export const useSurvey = () => {
  const brandId = useSelector(UserContextSelectors.brandId);
  const isAuthenticated = useSelector(UserContextSelectors.isAuthenticated);
  const isPromotionRestricted = useSelector(
    UserContextSelectors.isPromotionRestricted
  );
  const isInSurveyFeature = useFeature(AppFeatures.SURVEY);

  const isSurveyEnabled = useMemo(() => {
    const { clientPage } = PageContextUtils.getSurveyData();

    return isInSurveyFeature && !!clientPage;
  }, [isInSurveyFeature]);

  const isSurveyVisible = useMemo(() => {
    const { isVisible } = PageContextUtils.getSurveyData();
    if (isInSurveyFeature && isVisible) {
      return brandId === Brand.Sport_ES
        ? !isPromotionRestricted && isAuthenticated
        : true;
    }

    return false;
  }, [isInSurveyFeature, isPromotionRestricted, isAuthenticated, brandId]);

  const findIfSurveyIsVisibleByUrl = useCallback(
    url => {
      const { clientPage, oldClientPage } = PageContextUtils.getSurveyData();

      const surveyUrls =
        (isSurveyEnabled &&
          [clientPage, oldClientPage].filter(link => !!link)) ||
        [];

      if (!Array.isArray(surveyUrls)) return false;

      const { pathname, hash } = new URL(url, window.location.origin);

      return surveyUrls?.some(surveyUrl => {
        const { pathname: surveyPathName, hash: surveyHash } = new URL(
          UrlUtils.getSEOUrlWithoutLang(surveyUrl),
          window.location.origin
        );

        const pathWithoutLang = UrlUtils.getURLWithoutLang(pathname);

        return (
          (pathWithoutLang &&
            pathWithoutLang !== ROOT &&
            pathWithoutLang === surveyPathName) ||
          (hash && hash === surveyHash)
        );
      });
    },
    [isSurveyEnabled]
  );

  const surveyPages = useMemo(() => {
    const { clientPage, oldClientPage } = PageContextUtils.getSurveyData();

    return (
      (isSurveyEnabled && [clientPage, oldClientPage].filter(link => !!link)) ||
      []
    );
  }, [isSurveyEnabled]);

  const sdkUrl = useMemo(() => {
    const { sdkUrl } = PageContextUtils.getSurveyData();
    return sdkUrl;
  }, []);

  return {
    isInSurveyFeature,
    isSurveyEnabled,
    isSurveyVisible,
    sdkUrl,
    surveyPages,
    findIfSurveyIsVisibleByUrl
  };
};
